import React from "react";
import { Tooltip, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Reservation } from "../../../../utils/api/reservationsAPI";
import "./EventTableMap.scss";

type Props = {
  totalTables: number;
  reservations: Reservation[];
  onSelectFreeTable?: (tableNumber: number) => void;
  onEditReservation?: (reservation: Reservation) => void;
  canAdd?: boolean;
};

const PAYMENT_COLOR: Record<string, string> = {
  paid: "#10b981",
  partially_paid: "#f59e0b",
  unpaid: "#3b82f6",
};

const CARD_SIZE = 104;
const SEAT_RADIUS = 38;
const SEAT_SIZE = 12;
const MAX_SEATS = 10;

function getSeatPosition(index: number, total: number) {
  const angle = ((360 / total) * index - 90) * (Math.PI / 180);
  const cx = CARD_SIZE / 2;
  const cy = CARD_SIZE / 2;
  return {
    left: cx + SEAT_RADIUS * Math.cos(angle) - SEAT_SIZE / 2,
    top: cy + SEAT_RADIUS * Math.sin(angle) - SEAT_SIZE / 2,
  };
}

const EventTableMap: React.FC<Props> = ({
  totalTables,
  reservations,
  onSelectFreeTable,
  onEditReservation,
  canAdd = true,
}) => {
  const { t } = useTranslation();

  const tableMap = new Map<number, Reservation>();
  reservations.forEach((r) => {
    if (r.eventTableNumber && !["cancelled", "no-show"].includes(r.status)) {
      tableMap.set(r.eventTableNumber, r);
    }
  });

  const unassigned = reservations.filter(
    (r) => !r.eventTableNumber && !["cancelled", "no-show"].includes(r.status)
  );

  const tables = Array.from({ length: totalTables }, (_, i) => i + 1);

  return (
    <div className="etm">
      <div className="etm__grid">
        {tables.map((num) => {
          const reservation = tableMap.get(num);
          const isOccupied = !!reservation;

          if (isOccupied && reservation) {
            const seatCount = reservation.ticketsCount || reservation.numberOfGuests || 1;
            const shownSeats = Math.min(seatCount, MAX_SEATS);
            const overflow = seatCount > MAX_SEATS ? seatCount - MAX_SEATS : 0;
            const paymentColor = PAYMENT_COLOR[reservation.paymentStatus || "unpaid"];
            const isClickable = !!onEditReservation;

            return (
              <Tooltip
                key={num}
                title={
                  <div>
                    <div style={{ fontWeight: 600 }}>{reservation.guestName}</div>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>{reservation.guestPhone}</div>
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {seatCount} {seatCount === 1 ? t("person") : t("people")}
                    </div>
                  </div>
                }
                placement="top"
              >
                <div
                  className={`etm__table etm__table--occupied${isClickable ? " etm__table--clickable-edit" : ""}`}
                  style={{ width: CARD_SIZE, height: CARD_SIZE }}
                  onClick={() => onEditReservation?.(reservation)}
                >
                  {/* Seat dots */}
                  {Array.from({ length: shownSeats }, (_, i) => {
                    const pos = getSeatPosition(i, shownSeats);
                    return (
                      <div
                        key={i}
                        className="etm__seat etm__seat--occupied"
                        style={{
                          left: pos.left,
                          top: pos.top,
                          background: paymentColor,
                          width: SEAT_SIZE,
                          height: SEAT_SIZE,
                        }}
                      />
                    );
                  })}
                  {overflow > 0 && (
                    <div className="etm__seat-overflow">+{overflow}</div>
                  )}

                  {/* Table circle */}
                  <div className="etm__table-circle etm__table-circle--occupied" style={{ borderColor: paymentColor }}>
                    <span className="etm__table-num">{num}</span>
                    <span className="etm__table-count">{seatCount}</span>
                  </div>
                </div>
              </Tooltip>
            );
          }

          // Free table
          return (
            <div
              key={num}
              className={`etm__table etm__table--free${canAdd && onSelectFreeTable ? " etm__table--clickable" : ""}`}
              style={{ width: CARD_SIZE, height: CARD_SIZE }}
              onClick={() => canAdd && onSelectFreeTable?.(num)}
            >
              <div className="etm__table-circle etm__table-circle--free">
                <span className="etm__table-num">{num}</span>
                {canAdd && onSelectFreeTable
                  ? <PlusOutlined className="etm__table-add" />
                  : <span className="etm__table-free-label">{t("Free")}</span>
                }
              </div>
            </div>
          );
        })}
      </div>

      {unassigned.length > 0 && (
        <div className="etm__unassigned">
          <span className="etm__unassigned-label">{t("No table assigned")}:</span>
          {unassigned.map((r) => (
            <Tag key={r._id} color="default" style={{ fontSize: 12 }}>
              {r.guestName} ({r.ticketsCount || 1})
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventTableMap;
